// File: src/pages/Trolleys.jsx

import React, { useEffect, useMemo, useState } from "react";
import { styled } from '@mui/material/styles';
import AdminLayout from "../components/AdminLayout";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
  onSnapshot,
  query,
  orderBy,
} from "firebase/firestore";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  IconButton,
  Chip,
  Box,
  Typography,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Tooltip,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Build as BuildIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  CleaningServices as CleaningServicesIcon,
  ShoppingCart as TrolleyIcon,
  Add as AddIcon,
} from "@mui/icons-material";

// Styled Components
const TrolleysContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: var(--space-xl);
`;

const TrolleysHeader = styled('div')`
  margin-bottom: var(--space-lg);
`;

const PageTitle = styled('h1')`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 var(--space-sm);
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: var(--space-md);

  svg {
    width: 40px;
    height: 40px;
    color: var(--accent-primary);
  }
`;

const PageSubtitle = styled('p')`
  font-size: 1rem;
  color: var(--text-secondary);
  margin: 0;
  font-weight: 400;
`;

const StatsGrid = styled(Stack)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: var(--space-lg);
  margin-bottom: var(--space-xl);
`;

const StatCard = styled(Box)`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: var(--space-lg);
  transition: all var(--transition-normal);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px var(--shadow);
    border-color: var(--accent-primary);
  }
`;

const StyledTableContainer = styled(TableContainer)`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;

  .MuiTable-root {
    background: transparent;
  }

  .MuiTableHead-root {
    background: var(--bg-tertiary);
  }

  .MuiTableCell-head {
    background: var(--bg-tertiary);
    color: var(--text-primary);
    font-weight: 700;
    border-bottom: 1px solid var(--border);
  }

  .MuiTableRow-root {
    border-bottom: 1px solid var(--border-subtle);

    &:hover {
      background: var(--surface-hover);
    }
  }

  .MuiTableCell-body {
    color: var(--text-secondary);
    border-bottom: none;
  }
`;

// Dark theme for Material-UI components
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#10b981',
      dark: '#059669',
      light: '#34d399',
    },
    secondary: {
      main: '#06b6d4',
    },
    success: {
      main: '#22c55e',
    },
    warning: {
      main: '#f59e0b',
    },
    error: {
      main: '#ef4444',
    },
    info: {
      main: '#06b6d4',
    },
    background: {
      default: '#0a0f0a',
      paper: '#2d342d',
    },
    text: {
      primary: '#f0f9f0',
      secondary: '#c8d8c8',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          textTransform: 'none',
          fontWeight: 600,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
          },
        },
        outlined: {
          borderColor: '#10b981',
          color: '#10b981',
          '&:hover': {
            borderColor: '#34d399',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          fontWeight: 600,
        },
        colorPrimary: {
          backgroundColor: 'rgba(16, 185, 129, 0.2)',
          color: '#10b981',
          borderColor: '#10b981',
        },
        colorSuccess: {
          backgroundColor: 'rgba(34, 197, 94, 0.2)',
          color: '#22c55e',
          borderColor: '#22c55e',
        },
        colorWarning: {
          backgroundColor: 'rgba(245, 158, 11, 0.2)',
          color: '#f59e0b',
          borderColor: '#f59e0b',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#2d342d',
          backgroundImage: 'none',
          border: '1px solid #374151',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#10b981',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#10b981',
            },
          },
        },
      },
    },
  },
});

const statusConfig = {
  available: { label: "Available", color: "success", icon: <CheckCircleIcon /> },
  paired: { label: "Paired", color: "primary", icon: <PersonIcon /> },
  maintenance: { label: "Maintenance", color: "warning", icon: <BuildIcon /> },
};

function Trolleys() {
  const [trolleys, setTrolleys] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [bulkDialog, setBulkDialog] = useState({ open: false, action: null });
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  const [formData, setFormData] = useState({
    trolley_id: "",
    status: "available",
  });

  // Real-time trolley data
  useEffect(() => {
    const q = query(collection(db, "trolleys"), orderBy("trolley_id"));
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const trolleyData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTrolleys(trolleyData);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching trolleys:", error);
        setSnack({ open: true, msg: `Failed to load trolleys: ${error.message}`, severity: "error" });
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Load users for reference
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "users"));
        const userData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(userData);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, []);

  const userNameById = useMemo(() => {
    const map = new Map();
    users.forEach((user) => {
      map.set(user.id, user.name || user.email || "Unknown User");
    });
    return map;
  }, [users]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateTrolleyId = (trolleyId, currentEditId = null) => {
    if (!trolleyId || trolleyId.trim() === "") {
      return "Trolley ID is required";
    }

    const trimmed = trolleyId.trim();
    if (trimmed.length < 1 || trimmed.length > 20) {
      return "Trolley ID must be between 1-20 characters";
    }

    const regex = /^[A-Za-z0-9-_]+$/;
    if (!regex.test(trimmed)) {
      return "Trolley ID can only contain letters, numbers, hyphens, and underscores";
    }

    const duplicate = trolleys.find(
      (t) => t.id !== currentEditId && t.trolley_id === trimmed
    );
    if (duplicate) {
      return "Trolley ID already exists";
    }

    return null;
  };

  const handleSave = async () => {
    const validation = validateTrolleyId(formData.trolley_id, editId);
    if (validation) {
      setSnack({ open: true, msg: validation, severity: "error" });
      return;
    }

    try {
      const trolleyData = {
        trolley_id: formData.trolley_id.trim(),
        status: formData.status,
        paired_user: null,
        paired_cart: null,
        paired_at: null,
        expires_at: null,
        last_sync: new Date(),
      };

      if (editId) {
        // Get current trolley data to preserve pairing if status isn't changing
        const currentTrolley = trolleys.find(t => t.id === editId);
        if (currentTrolley && currentTrolley.status === "paired" && formData.status === "paired") {
          // Preserve pairing data when editing a paired trolley
          trolleyData.paired_user = currentTrolley.paired_user;
          trolleyData.paired_cart = currentTrolley.paired_cart;
          trolleyData.paired_at = currentTrolley.paired_at;
          trolleyData.expires_at = currentTrolley.expires_at;
        }
        
        await updateDoc(doc(db, "trolleys", editId), trolleyData);
        setSnack({ open: true, msg: "Trolley updated successfully", severity: "success" });
      } else {
        const docId = `trolley_${formData.trolley_id.trim()}`;
        await setDoc(doc(db, "trolleys", docId), trolleyData);
        setSnack({ open: true, msg: "Trolley created successfully", severity: "success" });
      }

      setOpen(false);
      resetForm();
    } catch (error) {
      console.error("Error saving trolley:", error);
      setSnack({ open: true, msg: `Failed to save trolley: ${error.message}`, severity: "error" });
    }
  };

  const handleEdit = (trolley) => {
    setFormData({
      trolley_id: trolley.trolley_id,
      status: trolley.status,
    });
    setEditId(trolley.id);
    setOpen(true);
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await deleteDoc(doc(db, "trolleys", deleteId));
      setSnack({ open: true, msg: "Trolley deleted successfully", severity: "success" });
      setDeleteDialogOpen(false);
      setDeleteId(null);
    } catch (error) {
      console.error("Error deleting trolley:", error);
      setSnack({ open: true, msg: `Failed to delete trolley: ${error.message}`, severity: "error" });
    }
  };

  const resetForm = () => {
    setFormData({
      trolley_id: "",
      status: "available",
    });
    setEditId(null);
  };

  const handleBulkAction = async (action) => {
    try {
      let updateData = {};
      let actionText = "";

      switch (action) {
        case "set_available":
          updateData = {
            status: "available",
            paired_user: null,
            paired_cart: null,
            paired_at: null,
            expires_at: null,
            last_sync: new Date(),
          };
          actionText = "set to available";
          break;
        case "set_maintenance":
          updateData = {
            status: "maintenance",
            paired_user: null,
            paired_cart: null,
            paired_at: null,
            expires_at: null,
            last_sync: new Date(),
          };
          actionText = "set to maintenance";
          break;
        case "cleanup_expired":
          // This will be handled differently - only update expired paired trolleys
          const now = new Date();
          const expiredTrolleys = trolleys.filter(
            (t) => t.status === "paired" && t.expires_at && t.expires_at.toDate() < now
          );
          
          for (const trolley of expiredTrolleys) {
            await updateDoc(doc(db, "trolleys", trolley.id), {
              status: "available",
              paired_user: null,
              paired_cart: null,
              paired_at: null,
              expires_at: null,
              last_sync: new Date(),
            });
          }
          
          setSnack({ 
            open: true, 
            msg: `Cleaned up ${expiredTrolleys.length} expired trolley sessions`, 
            severity: "success" 
          });
          setBulkDialog({ open: false, action: null });
          return;
        default:
          return;
      }

      // Apply bulk update to all trolleys
      const updatePromises = trolleys.map((trolley) =>
        updateDoc(doc(db, "trolleys", trolley.id), updateData)
      );

      await Promise.all(updatePromises);
      setSnack({ 
        open: true, 
        msg: `All trolleys ${actionText} successfully`, 
        severity: "success" 
      });
    } catch (error) {
      console.error(`Error performing bulk action ${action}:`, error);
      setSnack({ 
        open: true, 
        msg: `Failed to perform bulk action: ${error.message}`, 
        severity: "error" 
      });
    }

    setBulkDialog({ open: false, action: null });
  };

  const getStatusChip = (status) => {
    const config = statusConfig[status] || statusConfig.available;
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    );
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return new Intl.DateTimeFormat("en-MY", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    } catch (error) {
      return "Invalid Date";
    }
  };

  const stats = useMemo(() => {
    const available = trolleys.filter((t) => t.status === "available").length;
    const paired = trolleys.filter((t) => t.status === "paired").length;
    const maintenance = trolleys.filter((t) => t.status === "maintenance").length;
    const expired = trolleys.filter(
      (t) => t.status === "paired" && t.expires_at && t.expires_at.toDate() < new Date()
    ).length;

    return { total: trolleys.length, available, paired, maintenance, expired };
  }, [trolleys]);

  if (loading) {
    return (
      <ThemeProvider theme={darkTheme}>
        <AdminLayout>
          <TrolleysContainer>
            <TrolleysHeader>
              <PageTitle>
                <TrolleyIcon />
                Loading trolleys...
              </PageTitle>
            </TrolleysHeader>
          </TrolleysContainer>
        </AdminLayout>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <AdminLayout>
        <TrolleysContainer>
          <TrolleysHeader>
            <PageTitle>
              <TrolleyIcon />
              Trolley Management
            </PageTitle>
            <PageSubtitle>
              Register and manage shopping trolleys. Users can pair with available trolleys through the mobile app.
            </PageSubtitle>
          </TrolleysHeader>

          {/* Statistics Cards */}
          <StatsGrid direction="row" spacing={2}>
            <StatCard>
              <Typography variant="h5" sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                {stats.total}
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                Total Trolleys
              </Typography>
            </StatCard>
            <StatCard>
              <Typography variant="h5" sx={{ color: 'var(--success)', fontWeight: 700 }}>
                {stats.available}
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                Available
              </Typography>
            </StatCard>
            <StatCard>
              <Typography variant="h5" sx={{ color: 'var(--accent-primary)', fontWeight: 700 }}>
                {stats.paired}
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                In Use
              </Typography>
            </StatCard>
            <StatCard>
              <Typography variant="h5" sx={{ color: 'var(--warning)', fontWeight: 700 }}>
                {stats.maintenance}
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                Maintenance
              </Typography>
            </StatCard>
            {stats.expired > 0 && (
              <StatCard sx={{ borderColor: 'var(--error)' }}>
                <Typography variant="h5" sx={{ color: 'var(--error)', fontWeight: 700 }}>
                  {stats.expired}
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                  Expired Sessions
                </Typography>
              </StatCard>
            )}
          </StatsGrid>

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
            >
              Add Trolley
            </Button>
          <Button 
            variant="outlined" 
            onClick={() => setBulkDialog({ open: true, action: "set_available" })}
          >
            Set All Available
          </Button>
          <Button 
            variant="outlined" 
            color="warning"
            onClick={() => setBulkDialog({ open: true, action: "set_maintenance" })}
          >
            Set All Maintenance
          </Button>
          {stats.expired > 0 && (
            <Button 
              variant="outlined" 
              color="error"
              startIcon={<CleaningServicesIcon />}
              onClick={() => setBulkDialog({ open: true, action: "cleanup_expired" })}
            >
              Cleanup Expired ({stats.expired})
            </Button>
          )}
        </Stack>

          {/* Trolleys Table */}
          <StyledTableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Trolley ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Paired User</TableCell>
                <TableCell>Paired At</TableCell>
                <TableCell>Expires At</TableCell>
                <TableCell>Last Sync</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trolleys.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No trolleys found. Add your first trolley to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                trolleys.map((trolley) => {
                  const isExpired = trolley.status === "paired" && 
                    trolley.expires_at && 
                    trolley.expires_at.toDate() < new Date();

                  return (
                    <TableRow 
                      key={trolley.id}
                      sx={{ backgroundColor: isExpired ? "error.light" : "inherit" }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {trolley.trolley_id}
                        </Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(trolley.status)}</TableCell>
                      <TableCell>
                        {trolley.paired_user ? (
                          <Tooltip title={`User ID: ${trolley.paired_user}`}>
                            <Typography variant="body2">
                              {userNameById.get(trolley.paired_user) || trolley.paired_user}
                            </Typography>
                          </Tooltip>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>{formatDate(trolley.paired_at)}</TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2"
                          color={isExpired ? "error.main" : "text.primary"}
                        >
                          {formatDate(trolley.expires_at)}
                          {isExpired && " (EXPIRED)"}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(trolley.last_sync)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Edit trolley">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEdit(trolley)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete trolley">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => confirmDelete(trolley.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          </StyledTableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? "Edit Trolley" : "Add Trolley"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Trolley ID"
              name="trolley_id"
              fullWidth
              value={formData.trolley_id}
              onChange={handleChange}
              placeholder="e.g., T001, CART-001, 123"
              helperText="Unique identifier for the trolley (1-20 characters, letters/numbers/hyphens/underscores only)"
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleChange}
                label="Status"
              >
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
              </Select>
            </FormControl>
            {editId && (
              <Alert severity="info">
                Note: Paired trolleys will lose their pairing if status is changed from "paired"
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {editId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this trolley? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Action Confirmation Dialog */}
      <Dialog open={bulkDialog.open} onClose={() => setBulkDialog({ open: false, action: null })}>
        <DialogTitle>Confirm Bulk Action</DialogTitle>
        <DialogContent>
          <Typography>
            {bulkDialog.action === "set_available" && 
              "Set all trolleys to Available status? This will unpair all currently paired trolleys."}
            {bulkDialog.action === "set_maintenance" && 
              "Set all trolleys to Maintenance status? This will unpair all currently paired trolleys."}
            {bulkDialog.action === "cleanup_expired" && 
              `Clean up ${stats.expired} expired trolley sessions? Expired paired trolleys will be reset to Available.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialog({ open: false, action: null })}>Cancel</Button>
          <Button 
            onClick={() => handleBulkAction(bulkDialog.action)} 
            color="warning" 
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity}>{snack.msg}</Alert>
      </Snackbar>
        </TrolleysContainer>
      </AdminLayout>
    </ThemeProvider>
  );
}

export default Trolleys;